import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { getApiError } from '@/types/apiError';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PART_FINDER_ACTIVITY_QUERY_KEY } from '../part-finder-activity/query';
import { PART_FINDER_REPORTS_QUERY_KEY } from '../part-finder-reports/query';
import { PART_FINDER_BATCH_QUERY_KEY, PART_FINDER_BATCHES_QUERY_KEY } from './query';

export const useDeletePartFinderBatches = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: string) => void;
} = {}) => {
  const queryClient = useQueryClient();
  const { getTenantId } = useTenants();
  const { mutate, isPending, error } = useMutation({
    mutationFn: async (batchIds: string[]) => {
      const tenantId = getTenantId();
      const results = await Promise.allSettled(batchIds.map(id => api.tenants.deletePartFinderBatch(tenantId, id)));
      const failures = results.filter((result): result is PromiseRejectedResult => result.status === 'rejected');
      if (failures.length === 0) return;

      const firstMessage =
        getApiError(failures[0].reason, 'Failed to delete batches')?.message ?? 'Failed to delete batches';
      const succeeded = batchIds.length - failures.length;
      throw new Error(
        succeeded > 0
          ? `Deleted ${succeeded} of ${batchIds.length} batches. ${failures.length} failed: ${firstMessage}`
          : firstMessage
      );
    },
    onSettled: () => {
      queryClient.resetQueries({ queryKey: [PART_FINDER_ACTIVITY_QUERY_KEY], exact: false });
      queryClient.resetQueries({ queryKey: [PART_FINDER_REPORTS_QUERY_KEY], exact: false });
      queryClient.resetQueries({ queryKey: [PART_FINDER_BATCHES_QUERY_KEY], exact: false });
      queryClient.resetQueries({ queryKey: [PART_FINDER_BATCH_QUERY_KEY], exact: false });
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error: Error) => {
      onError?.(error.message);
    },
  });

  return { mutate, isPending, error };
};
